import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { createSelector } from "@reduxjs/toolkit";
import { ALL_IGNORED_LABELS_LOWER_CASE, DEFAULT_SUBSCRIPTION_NAME_LEGACY_HOLOBUILDER, DEFAULT_SUBSCRIPTION_NAME_LEGACY_WEB_SHARE, LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE, LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE, VALUE_PRICING_MODEL_LABEL, WHITELISTED_IDENTIFIERS } from "@src/constants/subscription-constants";
import { runtimeConfig } from "@src/runtime-config";
import { FaroCoreApiConst, FaroCoreApiUtils } from "@stellar/core-api-client";
import { DateTimeUtils } from "@stellar/utils";
import { SdbCompaniesAdapter } from "@store/sdb-company/sdb-company-slice";
import { hasFeatureCompanyLevel, hasUserValidFeatureCompanyLevel } from "@utils/feature-control/company/company-feature-control";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { processSubscriptionLimits } from "@utils/sdb-company-utils";
import { getSubscriptionRemainingDays, isCompanyIdFromVPMList, isTrialSubscription } from "@utils/subscription-utils";
import { addNumberedEnv } from "@utils/url-utils";
/** Maximum end year, used to exclude subscriptions */
const MAX_PLAN_END_YEAR = 2099;

/**
 * Returns the ID of the selected SdbCompany
 */
export const selectedSdbCompanyIdSelector = createSelector(state => state.sdbCompany, sdbCompany => {
  return sdbCompany.selectedSdbCompanyId;
});

/**
 * Checks if the given id is the same as the currently selected sdbCompany
 */
export function isSdbCompanySelectedSelector(sdbCompanyId) {
  return createSelector(selectedSdbCompanyIdSelector, selectedId => {
    return selectedId === sdbCompanyId;
  });
}

/**
 * Returns the current selected SdbCompany
 */
export const selectedSdbCompanySelector = createSelector(selectedSdbCompanyIdSelector, state => state.sdbCompany, (sdbCompanyId, sdbCompany) => {
  if (!sdbCompanyId) {
    return null;
  }
  // Find the specific sdb-company using the id.
  return SdbCompaniesAdapter.getSelectors().selectById(sdbCompany, sdbCompanyId) ?? null;
});

/**
 * Returns the company type (category) of the selected workspace
 */
export const selectedSdbCompanyCategorySelector = createSelector(selectedSdbCompanySelector, selectedCompany => {
  return selectedCompany?.companyCategory;
});

/**
 * Returns the selected sdbCompany name
 */
export const selectedSdbCompanyNameSelector = createSelector(selectedSdbCompanySelector, selectedCompany => {
  return selectedCompany?.name ?? null;
});

/**
 * Returns all sdbCompany of the user
 */
export const sdbCompaniesSelector = createSelector(state => state.sdbCompany, state => state.ui.isHideMigratedWorkspacesEnabled, (sdbCompany, isHideMigratedWorkspacesEnabled) => {
  return SdbCompaniesAdapter.getSelectors().selectAll(sdbCompany)
  // Filters out migrated workspaces if the flag is enabled.
  // Do not do this on filteredSdbCompaniesSelector, so that the switch workspaces menu
  // can benefit from this filter.
  .filter(company => {
    if (isHideMigratedWorkspacesEnabled && company.isSphereLegacyMigrated) {
      return false;
    }
    return true;
  }).map(company => {
    let url = company.url;
    if (company.type === "company" && runtimeConfig.isNumberedEnv && runtimeConfig.numberedEnv) {
      url = addNumberedEnv(url, runtimeConfig.numberedEnv);
    }
    return {
      ...company,
      url
    };
  });
});

/**
 * Returns all sdbCompany of the user, filtered by the search text
 */
export const filteredSdbCompaniesSelector = createSelector(sdbCompaniesSelector, state => state.ui.search.debouncedSearchText, (companies, debouncedSearchText) => {
  const search = debouncedSearchText.toLowerCase();
  return companies.filter(company => company.name.toLowerCase().includes(search));
});

/**
 * Returns all Ids of sdbCompany of the user
 */
export const sdbCompanyIdsSelector = createSelector(state => state.sdbCompany, sdbCompany => {
  return SdbCompaniesAdapter.getSelectors().selectIds(sdbCompany);
});

/**
 * Returns whether the selected company has the given feature.
 *
 * @param featureIdentifier The identifier of the feature to check.
 * @returns True if the feature is enabled, false otherwise.
 */
export function isFeatureAvailableInCompanySelector(featureIdentifier) {
  return createSelector(state => availableFeaturesInCompanySelector(state), features => {
    return (features ?? []).includes(featureIdentifier);
  });
}

/**
 * Returns whether the selected workspace has the given feature.
 *
 * The backend reports a feature as `enabled` (the activation toggle) and `permitted` (whether the
 * current user may use it, resolved from the company and project permissions). A workspace counts
 * as having the feature only when both hold, which is how the backend composes its own feature
 * list, so the toggle on its own is not an entitlement answer.
 */
function hasCompanyFeatureSelector(featureIdentifier) {
  return createSelector(selectedCompanyContextSelector, companyContext => hasFeatureCompanyLevel({
    featuresAvailable: companyContext?.featuresAvailable ?? null,
    requiredCompanyFeatures: [featureIdentifier]
  }));
}

/**
 * Returns the fetching properties of the sdb-company slice.
 */
export const fetchingSdbCompanyFlagsSelector = createSelector(state => state.sdbCompany.fetching, fetching => {
  return fetching;
});

/**
 * Returns true if any fetching regarding company-slice is true.
 */
export const isFetchingSdbCompanySelector = createSelector(state => state.sdbCompany.fetching, fetching => {
  const {
    fetchSelectedCompanyContext,
    isFetchingSdbCompanies,
    isFetchingCompanyBrandingSetting,
    isFetchingCompanyCommunicationSettings,
    isFetchingCompanySubscriptions
  } = fetching;
  return fetchSelectedCompanyContext === "pending" || isFetchingCompanyBrandingSetting || isFetchingSdbCompanies || isFetchingCompanyCommunicationSettings || isFetchingCompanySubscriptions;
});

/**
 * Returns company context for the selected company.
 */
export const selectedCompanyContextSelector = createSelector(state => state.sdbCompany, sdbCompany => {
  return sdbCompany.selectedCompanyContext;
});

/**
 * Returns whether the user can use the teams feature in the selected workspace.
 * When false, teams must not be fetched or shown.
 */
export const canUseTeamsSelector = createSelector(selectedCompanyContextSelector, companyContext => hasUserValidFeatureCompanyLevel({
  featureName: RequiredFeatureCompanyLevelName.canUseTeams,
  featuresAvailable: companyContext?.featuresAvailable
}));

/**
 * Returns whether the selected company is on a Sphere XG 2026 Scan plan, on which only members
 * holding a Sphere XG seat can be editors or admins.
 *
 * Read from the company context rather than the subscriptions, since every member gets the context
 * while only enterprise admins may read the subscriptions.
 */
export const isScanPlanSelector = createSelector(selectedCompanyContextSelector, companyContext => !!companyContext?.featuresAvailable.some(feature => feature.enabled && Object.hasOwn(FaroCoreApiConst.scanBundleName, feature.identifier)));

/** Returns all company features for the selected company */
export const availableFeaturesInCompanySelector = createSelector(selectedCompanyContextSelector, companyContext => {
  if (!companyContext) {
    return null;
  }
  return companyContext.featuresAvailable.map(feature => feature.identifier);
});

/**
 * Returns branding settings for the selected company.
 */
export const companyBrandingSettingsSelector = createSelector(state => state.sdbCompany, sdbCompany => {
  return sdbCompany.brandingSettings;
});

/** Get company details by providing the companyId */
export function getCompanyByIdSelector(companyId) {
  return createSelector(state => state.sdbCompany, sdbCompany => {
    return SdbCompaniesAdapter.getSelectors().selectById(sdbCompany, companyId) ?? null;
  });
}

/**
 * Returns communication settings for the selected company.
 */
export const companyCommunicationSettingsSelector = createSelector(state => state.sdbCompany, sdbCompany => {
  return sdbCompany.communicationSettings;
});

/**
 * Returns whether the selected workspace was migrated from HoloBuilder to Sphere XG.
 * Reuses the company feature definition so this always agrees with the sidebar, which asks the
 * same question through `useHasUserValidFeatureCompanyLevel`.
 */
const isHbToSxgMigratedSelector = createSelector(selectedCompanyContextSelector, companyContext => hasUserValidFeatureCompanyLevel({
  featureName: RequiredFeatureCompanyLevelName.isHbToSxgMigrated,
  featuresAvailable: companyContext?.featuresAvailable
}));

/**
 * Returns whether the selected workspace may use the WebEditor (HoloBuilder).
 *
 * This is the single gate for every WebEditor affordance: the HoloBuilder dashboard switch in the
 * sidebar, the "Open in HoloBuilder" actions, the HoloBuilder branding section and the default
 * application setting. When it is false the workspace is Sphere XG exclusive, so naming the target
 * in the "Open" labels is redundant.
 */
export const canOpenWebEditorSelector = createSelector(hasCompanyFeatureSelector("open-in-webeditor"), isHbToSxgMigratedSelector, (hasOpenInWebEditor, isHbToSxgMigrated) => {
  return hasOpenInWebEditor && !isHbToSxgMigrated;
});

/**
 * Returns whether the selected workspace may capture 360° images.
 *
 * Checks write rather than read access: point-cloud-only plans can still read 360° images, so only
 * capture tells them apart from plans that include 360°.
 */
export const canWriteImg360Selector = hasCompanyFeatureSelector("global-img360-write");

/**
 * Returns the default open project target for the selected company, based on the company settings.
 * If the company settings are set, it will favour the company settings.
 * If the company settings are not set, it will favour the provided default target.
 * @param defaultTarget  default target if the company settings are not set.
 * @param isSphereViewerDefaultSetting  company setting for the default target. Null if not set.
 * @returns The application to open the projects in by default.
 */
export function getDefaultOpenProjectTarget(payload) {
  // Returns defaultTarget if the user has not set any target application.
  if (payload.isSphereViewerDefaultSetting === undefined || payload.isSphereViewerDefaultSetting === null) {
    return payload.defaultTarget;
  }
  return payload.isSphereViewerDefaultSetting ? ProjectLaunchTarget.sphereViewer : ProjectLaunchTarget.webEditor;
}

/** Returns the default open project application for the selected company. */
export const defaultProjectLaunchTargetSelector = createSelector(canOpenWebEditorSelector, hasCompanyFeatureSelector("sphere-dashboard"), companyCommunicationSettingsSelector, (canOpenWebEditor, hasSphereDashboard, communicationSettings) => {
  // Sphere XG exclusive workspaces have a single target, so a company setting left over from
  // when they still had the WebEditor must not send them there.
  if (!canOpenWebEditor) {
    return {
      defaultOpenProjectTarget: ProjectLaunchTarget.sphereViewer,
      canOpenWebEditor
    };
  }
  return {
    defaultOpenProjectTarget: getDefaultOpenProjectTarget({
      // Workspaces on a Sphere XG plan are promoted to Sphere Viewer; the WebEditor stays the
      // default only for those whose plan does not include the Sphere XG dashboard.
      defaultTarget: hasSphereDashboard ? ProjectLaunchTarget.sphereViewer : ProjectLaunchTarget.webEditor,
      isSphereViewerDefaultSetting: communicationSettings?.sphereViewerIsDefault
    }),
    canOpenWebEditor
  };
});

/**
 * Returns the subscriptions of the selected company
 */
export const selectedCompanySubscriptionsSelector = createSelector(state => state.sdbCompany, sdbCompany => {
  return sdbCompany.selectedCompanySubscriptions;
});

/**
 * Returns all credit subscriptions of the selected company
 */
export const allCreditSubscriptionsSelector = createSelector(selectedCompanySubscriptionsSelector, companySubscriptions => {
  if (!companySubscriptions.length) {
    return [];
  }
  const result = companySubscriptions.flatMap(subscription => processSubscriptionLimits(subscription));
  const descSortedResult = result.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  return descSortedResult;
});

/**
 * Returns all available credit subscriptions of the selected company
 */
export const availableCreditSubsSelector = createSelector(allCreditSubscriptionsSelector, allCreditSubscriptions => {
  if (!allCreditSubscriptions) {
    return [];
  }
  const now = new Date();
  return allCreditSubscriptions.filter(creditSub => creditSub.availableCredit > 0 && new Date(creditSub.expiresAt) > now);
});

/**
 * Returns the earliest expiring credits of the selected company
 */
export const getEarliestExpiringCreditsSelector = createSelector(availableCreditSubsSelector, availableSubscriptions => {
  if (availableSubscriptions.length === 0) {
    return undefined;
  }

  // Group subscriptions by expiration date and sum the available credits for each date
  const groupedByDate = new Map();
  for (const subscription of availableSubscriptions) {
    // Example: "2025-04-02T15:30:00Z" => "2025-04-02"
    const expirationDate = subscription.expiresAt.split("T")[0];
    const existingSubscription = groupedByDate.get(expirationDate);
    if (!existingSubscription) {
      groupedByDate.set(expirationDate, {
        totalCredits: subscription.availableCredit,
        expiresAt: subscription.expiresAt
      });
    } else {
      existingSubscription.totalCredits += subscription.availableCredit;
    }
  }

  // Find the earliest expiration date
  let earliest;
  for (const expiringSubscription of groupedByDate.values()) {
    if (!earliest || expiringSubscription.expiresAt < earliest.expiresAt) {
      earliest = expiringSubscription;
    }
  }
  return earliest;
});

/**
 * Returns the total credit balance of the company
 * This is the sum of all available credits of the subscriptions.
 */
export const companyCreditBalanceSelector = createSelector(availableCreditSubsSelector, availableSubscriptions => availableSubscriptions.reduce((total, {
  availableCredit
}) => total + availableCredit, 0));

/**
 * Sphere XG 2026 Scan subscriptions arrive with the bundle identifier (e.g. "sphere-xg-2026-scan-essential")
 * as their label or carried on a feature, instead of the human-readable name. This rewrites the label to the
 * readable XG label so detection, priority and display all resolve through the library.
 */
function withResolvedScanLabel(subscription) {
  const identifiers = new Set([subscription.label, ...subscription.features.map(feature => feature.identifier)]);

  // Match by entry so the readable label is taken from the value, keeping identifiers as plain
  // strings (no BundleIdentifier cast, no arbitrary-key lookup on the map).
  const scanLabel = Object.entries(FaroCoreApiConst.scanBundleName).find(([identifier, label]) => label && identifiers.has(identifier))?.[1];
  return scanLabel ? {
    ...subscription,
    label: scanLabel
  } : subscription;
}

/**
 * Returns the company subscription with the highest priority and with longer expiration date.
 */
export const topPriorityXgSubscriptionSelector = createSelector(selectedCompanySubscriptionsSelector, companySubscriptions => {
  // Filter subscriptions to only allow active Sphere XG subscriptions and
  // then sort them by priority and expiration date.
  const sortedSubscriptions = companySubscriptions.filter(subscription => subscription.status === "ACTIVE")
  // Resolve raw scan bundle identifiers to their readable XG label before detection.
  .map(withResolvedScanLabel)
  // This filter is also a type guard to FaroCoreApiTypes.XGCompanySubscription
  .filter(subscription => FaroCoreApiUtils.Feature.isXGSubscription(subscription)).sort(FaroCoreApiUtils.Feature.compareXGSubscriptionsByPriority);

  // Return the highest priority subscription
  return sortedSubscriptions?.[0];
});

/** All company subscriptions */

/**
 * Represents the minimal information of a company subscription.
 * Only includes the `label` and `endDate` fields, which are necessary
 * to identify and compare subscriptions without sending the full object.
 */

/** Create an empty custom subscription */
export function createCustomSubscription(subscription) {
  const originalLabelLowerCase = subscription?.label?.toLowerCase() ?? "";
  let displayLabel;
  if (LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE.has(originalLabelLowerCase)) {
    displayLabel = DEFAULT_SUBSCRIPTION_NAME_LEGACY_HOLOBUILDER;
  } else if (LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE.has(originalLabelLowerCase)) {
    displayLabel = DEFAULT_SUBSCRIPTION_NAME_LEGACY_WEB_SHARE;
  }
  return {
    label: displayLabel,
    endDate: subscription?.endDate
  };
}

/**
 * If there is no XG subscription assigned in the company, it tries to find a legacy subscription,
 * based on some factors like known legacy labels, or the furthest end date.
 */
export const customSubscriptionSelector = createSelector(selectedCompanySubscriptionsSelector, selectedSdbCompanyIdSelector, (subscriptions, selectedSdbCompanyId) => {
  // Step 1: Filter out non-active subscriptions, ignored labels and subscriptions with MAX_PLAN_END_YEAR
  const filteredSubscriptions = subscriptions.filter(sub => {
    if (sub.status !== "ACTIVE") {
      return false;
    }
    if (sub.label && ALL_IGNORED_LABELS_LOWER_CASE.has(sub.label.toLowerCase())) {
      return false;
    }
    if (sub.endDate && new Date(sub.endDate).getFullYear() === MAX_PLAN_END_YEAR) {
      return false;
    }
    return true;
  });

  // Step 2: Sort subscriptions by end date descending. The ones expiring the latest first.
  const sortedByEndDateDesc = filteredSubscriptions.sort((a, b) => DateTimeUtils.diffBetweenTwoDatesInDays(b.endDate, a.endDate));

  // Create a set of all legacy subscription labels in lowercase for easy lookup
  const allLegacyLabels = new Set([...LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE, ...LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE]);

  // Step 3: Check for known legacy subscriptions both Holobuilder and Sphere
  const legacySubscription = sortedByEndDateDesc.find(sub => sub.label && allLegacyLabels.has(sub.label.toLowerCase()));
  if (legacySubscription) {
    return createCustomSubscription(legacySubscription);
  }

  // Step 4: Check for subscription with "user_role_fm" feature
  // There is a special feature called user_role_fm that only paid subscriptions have.
  // This is not ideal because we should rely better on labels to whitelist known legacy subscriptions,
  // but to this point the subscriptions do not provide any other way to identify paid legacy subscriptions.
  const subscriptionWithUserRoleFm = sortedByEndDateDesc.find(subscription => subscription.features?.some(feature => feature.identifier === "user_role_fm"));
  if (subscriptionWithUserRoleFm) {
    return createCustomSubscription({
      // We consider such subscriptions as having the Value Pricing Model
      label: VALUE_PRICING_MODEL_LABEL,
      endDate: subscriptionWithUserRoleFm.endDate
    });
  }

  // Step 5: If you reach this point, it could be that companies have a VPM subscription assigned to a project,
  // The only way would be to fetch every single project and check their features, but could be thousands of projects.
  // TODO: We might include a specific feature to such companies to identify them better in the future.
  // For now we use the company id and a workaround to identify by company id.
  if (isCompanyIdFromVPMList({
    companyId: selectedSdbCompanyId
  })) {
    return createCustomSubscription({
      label: VALUE_PRICING_MODEL_LABEL
      // Since we identified them by company id, we don't have an end date.
    });
  }

  // Step 6: Return a default one, assuming there is no active subscription
  return createCustomSubscription();
});

/**
 * Selector to get the company's quotas filtered by a whitelist.
 *
 * Only quotas whose `identifier` is present in `WHITELISTED_IDENTIFIERS`
 * will be returned. This ensures that only relevant quotas are exposed
 * for UI components or business logic.
 */
export const selectQuotasSelector = createSelector(selectedCompanyContextSelector, context => {
  if (!context) {
    return [];
  }
  return context.quotas.filter(quota => WHITELISTED_IDENTIFIERS.includes(quota.identifier) &&
  // The active members constraint is meaningless when only the single admin is allowed.
  !(quota.identifier === "user_role" && quota.limit <= 1));
});

/**
 * Selector to see if user has core products enabled.
 *
 * the coreProduct is true only if the company or a project of it has an active subscription
 */
export const hasCoreProductSelector = createSelector(selectedCompanyContextSelector, context => {
  if (!context) {
    return false;
  }
  return context.hasCoreProduct;
});

/**
 * Returns the active subscription for a company, prioritizing top-priority XG subscriptions.
 * If active subscription doesn't exist, it checks whether the company has coreProduct and if yes, it shows holobuilder-legacy.
 * If none exists, returns a custom subscription (Plan FM, furthest end date, or a default placeholder).
 */
export const activeSubscriptionSelector = createSelector(topPriorityXgSubscriptionSelector, customSubscriptionSelector, hasCoreProductSelector, (topPrioritySubscription, customSubscription, hasCoreProduct) => {
  if (topPrioritySubscription) {
    return topPrioritySubscription;
  }
  if (customSubscription.label) {
    return customSubscription;
  }
  if (hasCoreProduct) {
    return {
      label: DEFAULT_SUBSCRIPTION_NAME_LEGACY_HOLOBUILDER
    };
  }
  return customSubscription;
});
/**
 * Selector to get the storage usage for the company.
 *
 * This combines data from two sources:
 * 1. The raw `sizeInBytes` from `context.storage.projects.total`.
 * 2. The storage quota from `context.quotas` (if available).
 */
export const selectStorageUsedSelector = createSelector(selectedCompanyContextSelector, context => {
  if (!context) {
    return null;
  }
  const storageFromQuotas = context.quotas.find(quotas => quotas.identifier === "storage");
  if (!storageFromQuotas) {
    return null;
  }
  return {
    used: storageFromQuotas.current ?? 0,
    max: storageFromQuotas.limit ?? undefined
  };
});

/** Returns the company subscription status */
export const subscriptionStatusSelector = createSelector(topPriorityXgSubscriptionSelector, isFeatureAvailableInCompanySelector("core-product"), (subscription, isCoreProductFeatureEnabled) => {
  const isTrial = isTrialSubscription(subscription);
  const subscriptionRemainingDays = getSubscriptionRemainingDays(subscription);
  if (subscriptionRemainingDays === null || subscriptionRemainingDays < 0) {
    return isTrial ? "trial-ended" : "subscription-ended";
  }
  if (isTrial) {
    return "trial-active";
  }
  return isCoreProductFeatureEnabled ? "subscription-active" : "subscription-ended";
});

/** Usage of the seats of a workspace */

/** Usage of the Scene software licenses (seats) of a workspace */

/** Returns the active subscriptions of the selected company */
const activeCompanySubscriptionsSelector = createSelector(selectedCompanySubscriptionsSelector, subscriptions => subscriptions.filter(({
  status
}) => status === "ACTIVE"));

/** Returns the constraints of a type that the active subscriptions of the selected company have */
function activeConstraintsSelector(type) {
  return createSelector(activeCompanySubscriptionsSelector, subscriptions => subscriptions.flatMap(({
    constraints
  }) => constraints.filter(constraint => constraint.type === type)));
}

/**
 * Returns whether the plan of the selected company limits its active users (the `USER_ROLE`
 * constraint), i.e. the members that hold a Sphere XG seat because of their roles.
 */
export const hasUserRoleConstraintSelector = createSelector(activeConstraintsSelector("USER_ROLE"), constraints => constraints.length > 0);

/**
 * Returns the usage of the Sphere XG seats that are assigned to members (the `ACTIVE_SEAT`
 * constraint), summed across the active subscriptions of the selected company, or null when its
 * plan does not include such seats.
 */
export const activeSeatUsageSelector = createSelector(activeConstraintsSelector("ACTIVE_SEAT"), sumSeatUsage);

/**
 * Returns how many Sphere XG seats of the workspace are still free to be assigned to a member, as
 * the `ACTIVE_SEAT` constraint of its plan counts them, or undefined when that is not known: the
 * subscriptions are not loaded (yet), or the plan has no such constraint.
 */
export const activeSeatsAvailableSelector = createSelector(activeSeatUsageSelector, usage => usage ? Math.max(usage.max - usage.used, 0) : undefined);

/**
 * Returns the usage of the SCENE seats as the plan counts them (the `SCENE_SEAT` constraint), summed
 * across the active subscriptions of the selected company, or null when its plan does not include
 * SCENE seats.
 */
export const sceneSeatUsageSelector = createSelector(activeConstraintsSelector("SCENE_SEAT"), sumSeatUsage);

/** Sums up the usage of seat constraints, or returns null when there is none */
function sumSeatUsage(constraints) {
  if (!constraints.length) {
    return null;
  }
  return constraints.reduce((usage, {
    data
  }) => ({
    used: usage.used + (data.current ?? 0),
    max: usage.max + data.max
  }), {
    used: 0,
    max: 0
  });
}